import { useMemo, useState } from 'react';
import {
  COLUMNS, PRIORITIES, type Priority, type Subtask, type Task, type TaskStatus, addDays, capitalize, daysSince, fmtAgo, fmtShort,
  localToday, nextBusinessDay, parseCapture, plural, statusName,
} from '@quadro/domain';
import { confirmAction } from '../../components/Confirm';
import { Icon } from '../../components/Icon';
import { Options } from '../../components/Options';
import { Sheet, SheetFooter, useAutoFocus } from '../../components/Sheet';
import { useRoutines, useTaskHistory, useTasks } from '../../data/queries';
import { Comments } from './Comments';
import { LinksField } from './LinksField';
import { closeSheet, openSheet } from '../../state/sheets';
import { useWorkspace } from '../../state/workspace';
import { EntityField } from '../orgmap/EntityField';
import { useCaptureContext } from '../shared/Capture';
import { useEntities } from '../shared/useMap';
import { classificationText } from './TaskCard';
import { useTaskActions } from './useTaskActions';
import { hud } from '../../components/Hud';

const newId = () => crypto.randomUUID();

export function TaskSheet({ id }: { id: string }) {
  const task = useTasks().data?.find(t => t.id === id);
  if (!task) return null;
  return <TaskForm key={id} task={task} />;
}

function TaskForm({ task: t }: { task: Task }) {
  const ws = useWorkspace();
  const ed = ws.canWrite;
  const actions = useTaskActions();
  const ents = useEntities();
  const ctx = useCaptureContext();
  const history = useTaskHistory(t.id).data ?? [];
  const routine = useRoutines().data?.find(r => r.id === t.routineId);
  const today = localToday();
  const titleRef = useAutoFocus<HTMLInputElement>();

  const [title, setTitle] = useState(t.title);
  const [status, setStatus] = useState<TaskStatus>(t.status);
  const [projectId, setProjectId] = useState(t.projectId ?? '');
  const [assigneeId, setAssigneeId] = useState(t.assigneeId ?? '');
  const [priority, setPriority] = useState<Priority>(t.priority);
  const [due, setDue] = useState(t.due ?? '');
  const [who, setWho] = useState(t.waiting?.who ?? '');
  const [followUp, setFollowUp] = useState(t.waiting?.followUpOn ?? nextBusinessDay(addDays(today, 3)));
  const [categoryId, setCategoryId] = useState(t.classification?.categoryId ?? '');
  const [subcategoryId, setSubcategoryId] = useState(t.classification?.subcategoryId ?? '');
  const [themeIds, setThemeIds] = useState(t.themeIds);
  const [entityIds, setEntityIds] = useState(t.entityIds);
  const [description, setDescription] = useState(t.description);
  const [links, setLinks] = useState(t.links);
  const [subtasks, setSubtasks] = useState<Subtask[]>(t.subtasks);
  const [newSub, setNewSub] = useState('');

  const people = useMemo(() => {
    const ids = new Set([...ws.members.map(p => p.id), t.assigneeId, ...t.subtasks.map(s => s.assigneeId)].filter(Boolean) as string[]);
    return [...ids].map(i => [i, ws.person(i).name] as [string, string]);
  }, [ws, t]);
  const category = ws.settings.categories.find(c => c.id === categoryId);
  const doneSubs = subtasks.filter(s => s.done).length;
  const patchSub = (sid: string, p: Partial<Subtask>) => setSubtasks(list => list.map(s => (s.id === sid ? { ...s, ...p } : s)));

  let info = `Criada ${fmtAgo(t.createdAt)}${t.createdById ? ` por ${ws.person(t.createdById).name}` : ''}. Em “${statusName(t.status)}” há ${plural(daysSince(t.enteredStatusAt), 'dia', 'dias')}.`;
  if (t.status === 'done' && t.startedAt && t.completedAt) info += ` Ciclo de ${Math.max(1, Math.round((Date.parse(t.completedAt) - Date.parse(t.startedAt)) / 864e5))} dias do início à entrega.`;

  function save() {
    if (!title.trim()) return;
    const changes: string[] = [];
    const patch: Partial<Task> = {};
    if (title.trim() !== t.title) { patch.title = title.trim(); changes.push('renomeada'); }
    if ((projectId || null) !== t.projectId) { patch.projectId = projectId || null; changes.push(projectId ? `projeto ${ws.settings.projects.find(p => p.id === projectId)?.name}` : 'sem projeto'); }
    if ((assigneeId || null) !== t.assigneeId) { patch.assigneeId = assigneeId || null; changes.push(assigneeId ? `atribuída a ${ws.person(assigneeId).name}` : 'responsável removido'); }
    if (priority !== t.priority) { patch.priority = priority; changes.push(`prioridade ${PRIORITIES[priority].toLowerCase()}`); }
    if ((due || null) !== t.due) { patch.due = due || null; changes.push(due ? `prazo para ${fmtShort(due)}` : 'prazo removido'); }
    if (description !== t.description) { patch.description = description; changes.push('descrição editada'); }
    const cleanLinks = links.map(l => ({ url: l.url, title: l.title.trim() || l.url }));
    if (JSON.stringify(cleanLinks) !== JSON.stringify(t.links)) {
      patch.links = cleanLinks;
      const added = cleanLinks.filter(l => !t.links.some(o => o.url === l.url)), removed = t.links.filter(o => !cleanLinks.some(l => l.url === o.url));
      changes.push(added.length ? `link ${added.map(l => `“${l.title}”`).join(', ')} adicionado` : removed.length ? 'link removido' : 'links renomeados');
    }
    if ([...themeIds].sort().join() !== [...t.themeIds].sort().join()) { patch.themeIds = themeIds; changes.push('temas atualizados'); }
    if (entityIds.join() !== t.entityIds.join()) { patch.entityIds = entityIds; changes.push(entityIds.length ? `entidades: ${entityIds.map(ents.name).join(', ')}` : 'entidades removidas'); }
    const cls = categoryId ? { categoryId, subcategoryId: subcategoryId || null } : null;
    if (JSON.stringify(cls) !== JSON.stringify(t.classification)) {
      patch.classification = cls;
      changes.push(cls ? `classificada como ${classificationText({ ...t, classification: cls }, ws)}` : 'classificação removida');
    }
    const subs = subtasks.filter(s => s.text.trim());
    if (JSON.stringify(subs) !== JSON.stringify(t.subtasks)) {
      const finished = subs.filter(s => s.done && !t.subtasks.find(a => a.id === s.id)?.done).length;
      patch.subtasks = subs;
      changes.push(finished ? plural(finished, 'subtarefa concluída', 'subtarefas concluídas') : 'subtarefas atualizadas');
    }
    const waiting = { who: who.trim() || 'Terceiros', followUpOn: followUp || null };
    if (status === 'waiting' && (t.status !== 'waiting' || JSON.stringify(waiting) !== JSON.stringify(t.waiting))) patch.waiting = waiting;
    if (status !== t.status) patch.status = status;
    if (Object.keys(patch).length) actions.save(t.id, patch, changes.length ? capitalize(changes.join(', ')) : undefined);
    if (status === 'done' && t.status !== 'done') hud('Tarefa finalizada');
    closeSheet();
  }

  function addSubtask() {
    if (!newSub.trim()) return;
    const r = parseCapture(newSub, ctx);
    setSubtasks(list => [...list, { id: newId(), text: r.title || newSub.trim(), done: false, assigneeId: r.assigneeId, due: r.due }]);
    setNewSub('');
  }

  return (
    <Sheet wide onClose={closeSheet} label="Tarefa">
      <div className="sh-body">
        {t.routineId && (
          <p className="rec-tag"><Icon name="rec" /> {routine
            ? <>Rotina <button className="side-link inline" onClick={() => openSheet({ kind: 'routine', id: routine.id })}>{routine.title}</button></>
            : 'Criada por uma rotina'}{t.routineDate ? `, ocorrência de ${fmtShort(t.routineDate)}` : ''}</p>
        )}
        <input className="big-title" ref={titleRef} placeholder="Título da tarefa" aria-label="Título" maxLength={160} disabled={!ed}
          value={title} onChange={e => setTitle(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && ed) save(); }} />
        <div className="form two">
          <label htmlFor="cStatus">Etapa</label>
          <select id="cStatus" disabled={!ed} value={status} onChange={e => setStatus(e.target.value as TaskStatus)}><Options items={COLUMNS.map(c => [c.id, c.name])} /></select>
          <label htmlFor="cProj">Projeto</label>
          <select id="cProj" disabled={!ed} value={projectId} onChange={e => setProjectId(e.target.value)}><Options items={[['', 'Sem projeto'], ...ws.settings.projects.map(p => [p.id, p.name] as [string, string])]} /></select>
          <label htmlFor="cResp">Responsável</label>
          <select id="cResp" disabled={!ed} value={assigneeId} onChange={e => setAssigneeId(e.target.value)}><Options items={[['', 'Ninguém'], ...people]} /></select>
          <label htmlFor="cPrio">Prioridade</label>
          <select id="cPrio" disabled={!ed} value={priority} onChange={e => setPriority(Number(e.target.value) as Priority)}><Options items={PRIORITIES.map((p, i) => [i, p])} /></select>
          <label htmlFor="cPrazo">Prazo</label>
          <input type="date" id="cPrazo" disabled={!ed} value={due} onChange={e => setDue(e.target.value)} />
          <span /><span />
          {status === 'waiting' && (
            <div className="wait-box">
              <label htmlFor="cQuem">Aguardando</label>
              <input id="cQuem" disabled={!ed} placeholder="Quem precisa responder" maxLength={60} value={who} onChange={e => setWho(e.target.value)} />
              <label htmlFor="cCobrar">Cobrar em</label>
              <input type="date" id="cCobrar" disabled={!ed} value={followUp} onChange={e => setFollowUp(e.target.value)} />
            </div>
          )}
          {ws.settings.categories.length > 0 ? (
            <>
              <label htmlFor="cCat">Categoria</label>
              <select id="cCat" disabled={!ed} value={categoryId} onChange={e => { setCategoryId(e.target.value); setSubcategoryId(''); }}>
                <Options items={[['', 'Não classificada'], ...ws.settings.categories.map(c => [c.id, c.name] as [string, string])]} />
              </select>
              <label htmlFor="cSub">Subcategoria</label>
              <select id="cSub" disabled={!ed || !category} value={subcategoryId} onChange={e => setSubcategoryId(e.target.value)}>
                <Options items={[['', category ? 'Nenhuma' : '—'], ...(category?.subcategories ?? []).map(s => [s.id, s.name] as [string, string])]} />
              </select>
            </>
          ) : ed && (
            <><label>Categoria</label><div className="full"><span className="muted">Taxonomia não configurada.</span> <button type="button" className="side-link inline" onClick={() => openSheet({ kind: 'settings', tab: 'taxonomy' })}>Configurar</button></div></>
          )}
          <label>Temas</label>
          <div className="full tema-picker">
            {ws.settings.themes.length
              ? ws.settings.themes.map(th => (
                  <button key={th.id} type="button" className="tchip pick" style={{ '--tc': th.color } as React.CSSProperties} aria-pressed={themeIds.includes(th.id)} disabled={!ed}
                    onClick={() => setThemeIds(ids => (ids.includes(th.id) ? ids.filter(x => x !== th.id) : [...ids, th.id]))}>{th.name}</button>
                ))
              : <><span className="muted">Nenhum tema cadastrado.</span>{ed && <> <button type="button" className="side-link inline" onClick={() => openSheet({ kind: 'settings', tab: 'themes' })}>Criar temas</button></>}</>}
          </div>
          <label>Entidades</label>
          <div className="full ent-field"><EntityField value={entityIds} onChange={setEntityIds} editable={ed} /></div>
          <label>Links</label>
          <div className="full"><LinksField value={links} onChange={setLinks} editable={ed} /></div>
          <label htmlFor="cDesc">Descrição</label>
          <textarea id="cDesc" rows={3} className="full" disabled={!ed} placeholder="Contexto, critérios de pronto…" value={description} onChange={e => setDescription(e.target.value)} />
        </div>

        <h3>Subtarefas <span className="st-prog">{subtasks.length > 0 && <><span className="k-bar"><i style={{ width: `${(doneSubs / subtasks.length) * 100}%` }} /></span><span>{doneSubs} de {subtasks.length}</span></>}</span></h3>
        <div>
          {subtasks.length ? subtasks.map(s => (
            <div key={s.id} className={`st ${s.done ? 'feito' : ''}`}>
              <input type="checkbox" checked={s.done} disabled={!ed} aria-label="Concluir subtarefa" onChange={e => patchSub(s.id, { done: e.target.checked })} />
              <input className="st-t" value={s.text} disabled={!ed} aria-label="Subtarefa" onChange={e => patchSub(s.id, { text: e.target.value })} />
              <select className="st-r" disabled={!ed} aria-label="Responsável da subtarefa" value={s.assigneeId ?? ''} onChange={e => patchSub(s.id, { assigneeId: e.target.value || null })}><Options items={[['', 'Ninguém'], ...people]} /></select>
              <input type="date" className={`st-d ${!s.done && s.due && s.due < today ? 'late' : ''}`} disabled={!ed} aria-label="Prazo da subtarefa" value={s.due ?? ''} onChange={e => patchSub(s.id, { due: e.target.value || null })} />
              {ed ? <button className="x-btn" aria-label="Remover subtarefa" onClick={() => setSubtasks(list => list.filter(x => x.id !== s.id))}><Icon name="x" /></button> : <span />}
            </div>
          )) : <p className="muted st-vazio">Quebre a tarefa em partes menores, cada uma com responsável e prazo.</p>}
        </div>
        {ed && (
          <div className="st-add"><Icon name="plus" />
            <input placeholder="Nova subtarefa. Ex.: Revisar números @bruno sexta" aria-label="Nova subtarefa" value={newSub}
              onChange={e => setNewSub(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSubtask(); } }} />
          </div>
        )}
        <p className="info">{info}</p>
        <Comments taskId={t.id} />
        <details className="hist">
          <summary>Histórico ({history.length})</summary>
          <ul>{history.map((h, i) => <li key={i}><span className="muted">{fmtAgo(h.at)}{h.byId ? `, ${ws.person(h.byId).name}` : ''}:</span> {h.text}</li>)}</ul>
        </details>
      </div>
      <SheetFooter>
        {ed ? <button className="btn link" onClick={() => confirmAction({
          title: 'Excluir esta tarefa?', text: `“${t.title}” será excluída para toda a equipe, com subtarefas e histórico.`, action: 'Excluir',
          onConfirm: () => { closeSheet(); actions.remove(t.id); },
        })}>Excluir tarefa</button> : <span className="muted">Somente leitura</span>}
        {ed && <button className="btn link neutral" onClick={() => openSheet({ kind: 'templates', fromTask: t.id })} title="Cria um modelo com o título, descrição, links e subtarefas desta tarefa">Salvar como modelo</button>}
        <span className="grow" />
        <button className="btn" onClick={closeSheet}>{ed ? 'Cancelar' : 'Fechar'}</button>
        {ed && <button className="btn primary" disabled={!title.trim()} onClick={save}>Salvar</button>}
      </SheetFooter>
    </Sheet>
  );
}
